import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/system';
import { useLocation, useNavigate } from 'react-router-dom';

const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const api = axios.create({
  baseURL: isDevelopment
    ? 'http://localhost:5000'
    : 'https://www.satoshiadvance.com',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true
});

const RetroContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  backgroundColor: '#283593',
  padding: '20px',
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '90%',
  maxWidth: '400px',
  /* Include the padding in the width — without this the card renders
     wider than the phone screen and clips on both sides */
  boxSizing: 'border-box',
  boxShadow: `
    0 0 10px #FFFFFF,
    0 0 20px #00BFFF,
    0 0 30px #00BFFF`,
  outline: 'none',
  '@media (max-width: 600px)': {
    padding: '15px',
    width: '95%',
  },
}));

const RetroTitle = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontSize: '1.8rem',
  textShadow: `0 0 10px #FFFFFF, 0 0 20px #00BFFF, 0 0 30px #00BFFF`,
  fontFamily: "'Press Start 2P', cursive",
  marginBottom: '20px',
  '@media (max-width: 600px)': {
    fontSize: '0.9rem',
    lineHeight: '1.4',
  },
}));

const RetroTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontFamily: "'Press Start 2P', cursive",
    fontSize: '0.9rem',
    color: '#FFFFFF',
    backgroundColor: '#283593',
    width: '100%',
    margin: '10px 0',
    border: '2px solid #FFFFFF',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#3949ab',
      borderColor: '#FFFFFF',
    },
    '&.Mui-focused': {
      borderColor: '#FFFFFF',
    },
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    color: '#FFFFFF',
    padding: '15px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
}));

const RetroButton = styled(Button)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  fontSize: '1rem',
  color: '#FFFFFF',
  backgroundColor: '#283593',
  border: '2px solid #FFFFFF',
  borderRadius: '4px',
  padding: '10px 20px',
  margin: '20px 0',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#1a237e',
    borderColor: '#FFFFFF',
  },
}));

const ErrorText = styled(Typography)({
  color: '#ff4444',
  fontFamily: "'Press Start 2P', cursive",
  fontSize: '0.8rem',
  margin: '10px 0',
});

// Landing page for the password-reset email link:
// /reset-password?token=...&email=...
// One password works across the homepage and every mini game, so a reset
// completed here applies everywhere. On success the API returns a session
// and we log the user straight in.
const ResetPasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const email = params.get('email');
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This reset link is missing its token. Please request a new one from the login popup.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await api.post('/api/users/reset-password', {
        email,
        token,
        password
      });

      const loggedInEmail = response.data?.user?.email || email;
      setSuccess(true);

      // Log the user in with their new password everywhere on the site
      if (loggedInEmail) {
        const userData = {
          email: loggedInEmail,
          isVerified: true,
          lastLogin: new Date().toISOString()
        };
        localStorage.setItem('userEmail', loggedInEmail);
        sessionStorage.setItem('userEmail', loggedInEmail);
        localStorage.setItem('user', JSON.stringify(userData));
        sessionStorage.setItem('user', JSON.stringify(userData));
        document.cookie = `userEmail=${loggedInEmail}; domain=.satoshiadvance.com; path=/; max-age=86400`;
        if (response.data?.token) {
          document.cookie = `satoshiToken=${encodeURIComponent(response.data.token)}; domain=.satoshiadvance.com; path=/; max-age=86400; SameSite=Lax; Secure`;
        }
      }

      setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(
        err.response?.data?.error ||
        'Failed to reset password. The link may have expired — please request a new one.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#121212"
      sx={{ px: 2, boxSizing: 'border-box' }}
    >
      <RetroContainer>
        {success ? (
          <Typography
            sx={{
              color: '#FFFFFF',
              fontFamily: "'Press Start 2P', cursive",
              fontSize: '1rem',
              lineHeight: 1.8
            }}
          >
            Password updated! It works on the homepage and every mini game. Redirecting...
          </Typography>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <RetroTitle>Reset Your Password</RetroTitle>
            {email && (
              <Typography
                sx={{
                  color: '#9dd5ff',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '0.7rem',
                  mb: 1,
                  wordBreak: 'break-all'
                }}
              >
                {email}
              </Typography>
            )}
            {error && <ErrorText>{error}</ErrorText>}
            <RetroTextField
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              fullWidth
            />
            <RetroTextField
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              fullWidth
            />
            <RetroButton type="submit" disabled={loading || !token}>
              {loading ? (
                <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
              ) : (
                'Reset Password'
              )}
            </RetroButton>
          </Box>
        )}
      </RetroContainer>
    </Box>
  );
};

export default ResetPasswordPage;
